// Copyright (c) Microsoft. All rights reserved. Licensed under the MIT license.
import { generateCryptoSafeRandom } from './generateCryptoSafeRandom';

export interface IEndpointConfiguration {
  // Registered OAuth ClientID
  clientId?: string;

  // Base URL of the endpoint
  baseUrl?: string;

  // URL segment for OAuth authorize endpoint.
  // The final authorize url is constructed as (baseUrl + '/' + authorizeUrl).
  authorizeUrl?: string;

  // Registered OAuth redirect url.
  // Defaults to window.location.origin
  redirectUrl?: string;

  // Registered OAuth scope.
  scope?: string;

  // Resource parameter for the OAuth provider.
  resource?: string;

  // Automatically generate a state? defaults to false.
  state?: boolean;

  // Automatically generate a nonce? defaults to false.
  nonce?: boolean;

  // OAuth responseType.
  responseType?: string;

  // Additional object for query parameters.
  // Will be appending them after encoding the values.
  extraQueryParameters?: { [index: string]: string };
}

/**
 * Helper to generate the OAuth login url.
 * @param endpointConfig Valid Endpoint configuration.
 * @return Returns the added endpoint.
 */
export function getLoginParams(endpointConfig: IEndpointConfiguration): {
  url: string;
  state: number;
} {
  const scope = endpointConfig.scope
    ? encodeURIComponent(endpointConfig.scope)
    : null;
  const resource = endpointConfig.resource
    ? encodeURIComponent(endpointConfig.resource)
    : null;
  const state = endpointConfig.state && generateCryptoSafeRandom();
  const nonce = endpointConfig.nonce && generateCryptoSafeRandom();

  const urlSegments = [
    `response_type=${endpointConfig.responseType}`,
    `client_id=${encodeURIComponent(endpointConfig.clientId)}`,
    `redirect_uri=${encodeURIComponent(endpointConfig.redirectUrl)}`,
  ];

  if (scope) {
    urlSegments.push(`scope=${scope}`);
  }
  if (resource) {
    urlSegments.push(`resource=${resource}`);
  }
  if (state) {
    urlSegments.push(`state=${state}`);
  }
  if (nonce) {
    urlSegments.push(`nonce=${nonce}`);
  }
  if (endpointConfig.extraQueryParameters) {
    for (const param of Object.keys(endpointConfig.extraQueryParameters)) {
      urlSegments.push(
        `${param}=${encodeURIComponent(
          endpointConfig.extraQueryParameters[param]
        )}`
      );
    }
  }

  return {
    url: `${endpointConfig.baseUrl}${
      endpointConfig.authorizeUrl
    }?${urlSegments.join('&')}`,
    state: state,
  };
}
